import { initializeApp } from 'firebase/app';
import { getFirestore, collection, doc, writeBatch, deleteDoc, updateDoc, setDoc, getDocs, query, where, runTransaction } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getUnit } from './utils';

const firebaseConfig = {
  apiKey: "AIzaSyDAGnvDSNWGZSr-H8sBNUvPo3juYsY8YDU",
  authDomain: "riaz-farmhouse.firebaseapp.com",
  databaseURL: "https://riaz-farmhouse-default-rtdb.firebaseio.com",
  projectId: "riaz-farmhouse",
  storageBucket: "riaz-farmhouse.firebasestorage.app",
  messagingSenderId: "389388775116",
  appId: "1:389388775116:web:3034fa3fcaeda207ba5c69",
  measurementId: "G-Y8VV13KMHS"
};

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const auth = getAuth(app);

// Fields sensitive enough that they shouldn't be broadcast to every visitor of
// the public booking page. These are written to a separate `bookingPrivate`
// collection (keyed by the same doc id) that only the admin panel reads, so
// the realtime listener behind the public "Confirmed Bookings" list never
// receives them at all.
const PRIVATE_FIELDS = ['phone', 'nicNo', 'serialNo'];

// `bookings` is admin-read-only now (see firestore.rules) — even name/price
// is more than a stranger needs. The public site instead reads this shadow
// collection, one doc per booking (same id), holding only what the live
// availability check actually needs: date/unit/slotType/status, plus
// paymentDeadline so the client-side auto-expiry sweep (which can no longer
// read `bookings` at all) still knows when to self-expire a doc it already
// knows the id of. None of these five fields identify a person.
function buildAvailabilityDoc(bookingData) {
  return {
    date: bookingData.checkInDate,
    unit: getUnit(bookingData),
    slotType: bookingData.slotType,
    status: bookingData.status,
    paymentDeadline: bookingData.paymentDeadline || null,
  };
}

// bookingPrivate reads now require a signed-in admin (see firestore.rules).
// Writes can't require auth the same way — customers create their own
// bookingPrivate doc while never signed in — so the rule instead only
// allows the write when it's part of the same atomic batch as a matching
// `bookings/{id}` create (see the getAfter() check in firestore.rules). The
// same pattern now also covers the new `availability` doc.
export async function saveBooking(bookingData, { skipAutoSerial = false } = {}) {
  const bookingRef = doc(collection(db, 'bookings'));
  const publicData = { ...bookingData };
  // Physical slips already carry a staff-handwritten serial; bookings with
  // none (i.e. made on the website) get an auto-generated "W"-prefixed one —
  // see webSerial.js for why that prefix matters. quickBookSlot() below opts
  // out entirely: it doesn't know yet whether this will end up transcribed
  // from a physical slip (its own real serial) or not, and bookingPrivate
  // can never be overwritten once set — so it's safer to assign no serial at
  // all than to auto-assign one staff can't later replace with the real one.
  if (!publicData.serialNo && !skipAutoSerial) {
    const { getNextWebSerial } = await import('./webSerial');
    publicData.serialNo = await getNextWebSerial();
  }
  const privateData = {};
  for (const field of PRIVATE_FIELDS) {
    if (publicData[field] !== undefined) {
      privateData[field] = publicData[field];
      delete publicData[field];
    }
  }
  const batch = writeBatch(db);
  batch.set(bookingRef, publicData);
  if (Object.keys(privateData).length > 0) {
    batch.set(doc(db, 'bookingPrivate', bookingRef.id), privateData);
  }
  batch.set(doc(db, 'availability', bookingRef.id), buildAvailabilityDoc(publicData));
  await batch.commit();
  return { id: bookingRef.id, serialNo: privateData.serialNo };
}

// Every admin action that touches a booking's status, date, unit, or
// slotType must keep the public `availability` shadow doc in sync — nothing
// should ever update `bookings` directly and let the two drift apart.
export async function updateBooking(firestoreId, updates) {
  const batch = writeBatch(db);
  batch.update(doc(db, 'bookings', firestoreId), updates);

  const availUpdates = {};
  if ('status' in updates)      availUpdates.status = updates.status;
  if ('checkInDate' in updates) availUpdates.date = updates.checkInDate;
  if ('slotType' in updates)    availUpdates.slotType = updates.slotType;
  if ('unit' in updates || 'farmhouse' in updates) {
    availUpdates.unit = getUnit(updates);
  }
  if (Object.keys(availUpdates).length > 0) {
    batch.update(doc(db, 'availability', firestoreId), availUpdates);
  }

  await batch.commit();
}

// TRUE hard delete — for cleaning up obvious test/duplicate entries only.
// Normal cancellation should go through updateBooking(id, { status: 'cancelled', ... })
// instead, which keeps the record for accountability; this one erases it,
// including its phone/NIC in bookingPrivate.
export async function deleteBooking(firestoreId) {
  await Promise.all([
    deleteDoc(doc(db, 'bookings', firestoreId)),
    deleteDoc(doc(db, 'availability', firestoreId)),
    deleteDoc(doc(db, 'bookingPrivate', firestoreId)).catch(() => {}), // may not exist for every booking
  ]);
}

// Instantly claims a slot as booked with no guest details at all — for
// clearing a backlog of physical paper slips before go-live without staff
// having to transcribe every field up front. Reuses saveBooking()'s exact
// atomic create path (bookings + availability in one batch), so it gets the
// same already-verified guarantees a real booking does and shows up as
// booked on the public site / Quick-Check the instant it's clicked, exactly
// like createHold() does for holds. Unlike a hold, this never auto-expires —
// it IS a real booking, just missing its paperwork — so it stays booked
// until someone edits or cancels it.
export async function quickBookSlot({ unit, date, slotType }) {
  return saveBooking({
    name: '',
    checkInDate: date,
    slotType,
    unit: unit === 'gold' ? 'Gold' : 'Silver',
    farmhouse: unit,
    guestCount: '',
    totalPrice: 0,
    advance: 0,
    balance: 0,
    status: 'confirmed',
    paymentStatus: 'Pending',
    infoComplete: false,
    createdAt: new Date().toISOString(),
  }, { skipAutoSerial: true });
}

// Fills in phone/NIC/serial for a booking that didn't have them yet (e.g. one
// created via quickBookSlot()). bookingPrivate has `allow update: if false`
// in firestore.rules — once a field lands there it can never change — so
// this only ever writes a bookingPrivate doc that doesn't exist yet.
// Firestore's rules engine treats a write to a nonexistent doc id as a
// `create`, which is valid here since the linked bookings/{id} doc already
// has a string name + checkInDate (even a quick-booked one with name: ''),
// satisfying the getAfter() check without needing to be batched together.
export async function savePrivateInfo(firestoreId, { phone, nicNo, serialNo }) {
  const data = {};
  if (phone) data.phone = phone;
  if (nicNo) data.nicNo = nicNo;
  if (serialNo) data.serialNo = serialNo;
  if (Object.keys(data).length === 0) return;
  await setDoc(doc(db, 'bookingPrivate', firestoreId), data);
}

const HOLD_DURATION_MS = {
  '30min': 30 * 60 * 1000,
  '2hr': 2 * 60 * 60 * 1000,
  '2days': 2 * 24 * 60 * 60 * 1000,
  '7days': 7 * 24 * 60 * 60 * 1000,
  // 'indefinite' deliberately has no entry — it never auto-expires (see the
  // "Active Holds" list, which is what keeps it from ever going invisible).
};

// Which other slotTypes on the same unit+date would conflict with a given
// request — a 22hour hold/booking blocks both day and night, and vice versa.
const SLOT_CONFLICTS = {
  '10hour-day':   ['10hour-day', '22hour'],
  '10hour-night': ['10hour-night', '22hour'],
  '22hour':       ['10hour-day', '10hour-night', '22hour'],
};

// Deterministic doc ID (unit+date+slotType), one per exact slot — this is
// what makes the transaction below actually atomic. Two staff racing to
// hold the *same* slot both target the *same* document, so Firestore's
// transaction retry/serialization guarantees only one write wins; the loser
// gets a clean rejection instead of both silently succeeding. A random
// addDoc() id (the old behavior) can't provide that guarantee — two
// different random docs can't collide with each other no matter how fast
// they're both written.
function holdDocId(unit, date, slotType) {
  return `${unit}_${date}_${slotType}`;
}

function describeHoldAge(iso) {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  return `${Math.floor(hours / 24)} day${Math.floor(hours / 24) === 1 ? '' : 's'} ago`;
}

export async function createHold({ unit, date, slotType, heldBy, duration }) {
  const coveringSlots = SLOT_CONFLICTS[slotType];

  // 1) Real-booking conflict check — a plain query against the public
  // `availability` shadow, run BEFORE the transaction below. Firestore's
  // client SDK can't run an arbitrary query *inside* a transaction (only
  // get() on specific doc refs), so this one check can't be made perfectly
  // atomic with the write — a real booking created in the exact same
  // instant as this call is a residual, extremely narrow race the current
  // architecture doesn't fully close. Acceptable for a small staff tool;
  // worth knowing if this ever needs hardening further.
  const availSnap = await getDocs(query(collection(db, 'availability'), where('date', '==', date), where('unit', '==', unit)));
  const bookedConflict = availSnap.docs
    .map((d) => d.data())
    .find((a) => a.status !== 'expired' && a.status !== 'cancelled' && coveringSlots.includes(a.slotType));
  if (bookedConflict) {
    throw new Error(`This slot already has a real booking (status: ${bookedConflict.status}) — holds can't override a real booking. Cancel or edit that booking first if it's wrong.`);
  }

  // 2) Any conflicting ACTIVE hold, old random-id or new deterministic-id
  // alike — also a pre-transaction query, closing the gap for holds
  // created before this deterministic-id scheme existed.
  const holdsSnap = await getDocs(query(collection(db, 'holds'), where('date', '==', date), where('unit', '==', unit), where('status', '==', 'active')));
  const heldConflict = holdsSnap.docs.map((d) => d.data()).find((h) => coveringSlots.includes(h.slotType));
  if (heldConflict) {
    throw new Error(`Already held by ${heldConflict.heldBy} (placed ${describeHoldAge(heldConflict.createdAt)}). Ask them to release it first, or coordinate before holding again.`);
  }

  const createdAt = new Date();
  const durationMs = HOLD_DURATION_MS[duration];
  const expiresAt = durationMs ? new Date(createdAt.getTime() + durationMs).toISOString() : null;

  // 3) The atomic part: re-check + write the exact target slot inside a
  // transaction keyed by its deterministic id. This is what actually
  // prevents two simultaneous submissions on the SAME slot from both
  // succeeding — whichever transaction commits second sees the just-written
  // active hold and throws instead of overwriting it.
  const targetRef = doc(db, 'holds', holdDocId(unit, date, slotType));
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(targetRef);
    if (snap.exists() && snap.data().status === 'active') {
      const h = snap.data();
      throw new Error(`Already held by ${h.heldBy} (placed ${describeHoldAge(h.createdAt)}). Ask them to release it first, or coordinate before holding again.`);
    }
    tx.set(targetRef, {
      unit, date, slotType, heldBy, duration,
      createdAt: createdAt.toISOString(),
      expiresAt,
      status: 'active',
    });
  });
}

export async function releaseHold(holdId) {
  await updateDoc(doc(db, 'holds', holdId), { status: 'released' });
}

// One-time (but safe to re-run) fix for bookings that were created before the
// `availability` shadow collection existed — those have no companion doc, so
// the public site and Quick-Check both silently treat them as open. Runs
// from the already-signed-in admin's browser (AdminPanel already has the
// full `bookings` list loaded), so it never needs separate credentials.
// Re-running this on bookings that already have a correct availability doc
// is harmless — it just overwrites it with the same values.
export async function backfillAvailability(bookings) {
  const CHUNK_SIZE = 450; // stay under Firestore's 500-writes-per-batch limit
  let written = 0;
  for (let i = 0; i < bookings.length; i += CHUNK_SIZE) {
    const chunk = bookings.slice(i, i + CHUNK_SIZE).filter(b => b.firestoreId && b.checkInDate && b.slotType);
    if (chunk.length === 0) continue;
    const batch = writeBatch(db);
    chunk.forEach(b => {
      batch.set(doc(db, 'availability', b.firestoreId), buildAvailabilityDoc(b));
    });
    await batch.commit();
    written += chunk.length;
  }
  return written;
}
